'use client';
import React, { useState, useEffect, useRef } from 'react';
import DatePicker from './DatePicker';

const AIRPORTS: Record<string, string> = {
  'New York': 'JFK',
  'Los Angeles': 'LAX',
  Chicago: 'ORD',
  Houston: 'IAH',
  Miami: 'MIA',
  Dallas: 'DFW',
  Atlanta: 'ATL',
  'San Francisco': 'SFO',
  Seattle: 'SEA',
  Boston: 'BOS',
  'Las Vegas': 'LAS',
  Orlando: 'MCO',
  Denver: 'DEN',
  Phoenix: 'PHX',
  'Washington DC': 'DCA',
  London: 'LHR',
  Paris: 'CDG',
  Tokyo: 'NRT',
  Dubai: 'DXB',
  Toronto: 'YYZ',
  Cancun: 'CUN',
  'Mexico City': 'MEX',
  Rome: 'FCO',
  Bangkok: 'BKK',
  Sydney: 'SYD',
};

interface SearchWidgetProps {
  onSearch?: (params: SearchParams) => void;
  initialValues?: Partial<SearchParams>;
  prefillDestination?: string | null;
}

export interface SearchParams {
  tripType: 'roundtrip' | 'oneway';
  origin: string;
  destination: string;
  departDate: string;
  returnDate: string;
  adults: number;
  children: number;
  infants: number;
  cabinClass: string;
}

function formatDateForInput(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export default function SearchWidget({
  onSearch,
  initialValues,
  prefillDestination,
}: SearchWidgetProps) {
  const [tripType, setTripType] = useState<'roundtrip' | 'oneway'>('roundtrip');
  const [origin, setOrigin] = useState('');
  const [destination, setDestination] = useState('');
  const [departDate, setDepartDate] = useState('');
  const [returnDate, setReturnDate] = useState('');
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [infants, setInfants] = useState(0);
  const [cabinClass, setCabinClass] = useState('Economy');
  const [showTripDropdown, setShowTripDropdown] = useState(false);
  const [showPaxDropdown, setShowPaxDropdown] = useState(false);
  const [showClassDropdown, setShowClassDropdown] = useState(false);
  const [originSuggestions, setOriginSuggestions] = useState<{ name: string; code: string }[]>([]);
  const [destSuggestions, setDestSuggestions] = useState<{ name: string; code: string }[]>([]);
  const [isSearchingOrigin, setIsSearchingOrigin] = useState(false);
  const [isSearchingDest, setIsSearchingDest] = useState(false);
  const [originHasSearched, setOriginHasSearched] = useState(false);
  const [destHasSearched, setDestHasSearched] = useState(false);
  const [mounted, setMounted] = useState(false);
  const originInputRef = useRef<HTMLInputElement>(null);
  const destInputRef = useRef<HTMLInputElement>(null);

  const tripDropdownRef = useRef<HTMLDivElement>(null);
  const paxDropdownRef = useRef<HTMLDivElement>(null);
  const classDropdownRef = useRef<HTMLDivElement>(null);
  const originRef = useRef<HTMLDivElement>(null);
  const destRef = useRef<HTMLDivElement>(null);

  const originTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const destTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (tripDropdownRef.current && !tripDropdownRef.current.contains(event.target as Node)) {
        setShowTripDropdown(false);
      }
      if (paxDropdownRef.current && !paxDropdownRef.current.contains(event.target as Node)) {
        setShowPaxDropdown(false);
      }
      if (classDropdownRef.current && !classDropdownRef.current.contains(event.target as Node)) {
        setShowClassDropdown(false);
      }
      if (originRef.current && !originRef.current.contains(event.target as Node)) {
        setOriginSuggestions([]);
        setOriginHasSearched(false);
      }
      if (destRef.current && !destRef.current.contains(event.target as Node)) {
        setDestSuggestions([]);
        setDestHasSearched(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (originTimeoutRef.current) clearTimeout(originTimeoutRef.current);
      if (destTimeoutRef.current) clearTimeout(destTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    setMounted(true);
    const today = new Date();
    const dep = new Date(today);
    dep.setDate(dep.getDate() + 7);
    const ret = new Date(dep);
    ret.setDate(ret.getDate() + 7);
    setDepartDate(formatDateForInput(dep));
    setReturnDate(formatDateForInput(ret));
  }, []);

  // Sync initialValues
  useEffect(() => {
    if (initialValues) {
      if (initialValues.tripType) setTripType(initialValues.tripType as 'roundtrip' | 'oneway');
      if (initialValues.origin) setOrigin(initialValues.origin);
      if (initialValues.destination) setDestination(initialValues.destination);
      if (initialValues.departDate) setDepartDate(initialValues.departDate);
      if (initialValues.returnDate) setReturnDate(initialValues.returnDate);
      if (initialValues.adults !== undefined) setAdults(initialValues.adults);
      if (initialValues.children !== undefined) setChildren(initialValues.children);
      if (initialValues.infants !== undefined) setInfants(initialValues.infants);
      if (initialValues.cabinClass) setCabinClass(initialValues.cabinClass);
    }
  }, [initialValues]);

  // When a destination is prefilled (e.g. from footer link), set it and focus origin
  useEffect(() => {
    if (prefillDestination) {
      const code = AIRPORTS[prefillDestination];
      if (code) {
        setDestination(`${prefillDestination} (${code})`);
      } else {
        setDestination(prefillDestination);
      }
      setDestSuggestions([]);
      setOrigin('');
      setOriginSuggestions([]);
      // Small delay so the DOM updates before focusing
      setTimeout(() => {
        originInputRef.current?.focus();
      }, 100);
    }
  }, [prefillDestination]);

  const fetchOriginSuggestions = async (val: string) => {
    setIsSearchingOrigin(true);
    setOriginHasSearched(true);
    try {
      const res = await fetch(`/api/airports?name=${encodeURIComponent(val)}`);
      if (res.ok) {
        const json = await res.json();
        setOriginSuggestions(json.data || []);
      } else {
        setOriginSuggestions([]);
      }
    } catch (err) {
      console.error('Failed to fetch origin suggestions:', err);
      setOriginSuggestions([]);
    } finally {
      setIsSearchingOrigin(false);
    }
  };

  const fetchDestSuggestions = async (val: string) => {
    setIsSearchingDest(true);
    setDestHasSearched(true);
    try {
      const res = await fetch(`/api/airports?name=${encodeURIComponent(val)}`);
      if (res.ok) {
        const json = await res.json();
        setDestSuggestions(json.data || []);
      } else {
        setDestSuggestions([]);
      }
    } catch (err) {
      console.error('Failed to fetch destination suggestions:', err);
      setDestSuggestions([]);
    } finally {
      setIsSearchingDest(false);
    }
  };

  const handleOriginChange = (val: string) => {
    setOrigin(val);
    if (originTimeoutRef.current) {
      clearTimeout(originTimeoutRef.current);
    }
    if (val.trim().length >= 2) {
      originTimeoutRef.current = setTimeout(() => {
        fetchOriginSuggestions(val);
      }, 300);
    } else {
      setOriginSuggestions([]);
      setOriginHasSearched(false);
    }
  };

  const handleDestChange = (val: string) => {
    setDestination(val);
    if (destTimeoutRef.current) {
      clearTimeout(destTimeoutRef.current);
    }
    if (val.trim().length >= 2) {
      destTimeoutRef.current = setTimeout(() => {
        fetchDestSuggestions(val);
      }, 300);
    } else {
      setDestSuggestions([]);
      setDestHasSearched(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const searchData = {
      tripType,
      origin,
      destination,
      departDate,
      returnDate,
      adults,
      children,
      infants,
      cabinClass,
    };

    // Log search data in the background (fire and forget)
    fetch('/api/search-logs', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...searchData,
        userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : undefined,
      }),
    }).catch((err) => console.error('Failed to log search:', err));

    if (onSearch) {
      onSearch(searchData);
    }
  };

  const totalTravelers = adults + children + infants;

  const cabinClasses = ['Economy', 'Premium Economy', 'Business Class', 'First Class'];

  if (!mounted) return null;

  return (
    <form
      onSubmit={handleSearch}
      className="glass-panel-dark p-3 sm:p-6 md:p-8 rounded-[20px] sm:rounded-2xl md:rounded-3xl shadow-premium border border-white/10 text-sm animate-fadeInUp flex flex-col"
    >


      {/* Row 1: Trip type, Travelers, Class Selection Pill Bars */}
      <div className="grid grid-cols-3 sm:flex sm:flex-wrap gap-1.5 sm:gap-2.5 mb-4 sm:mb-5">
        {/* Trip type selector */}
        <div ref={tripDropdownRef} className="relative">
          <button
            type="button"
            onClick={() => {
              setShowTripDropdown(!showTripDropdown);
              setShowPaxDropdown(false);
              setShowClassDropdown(false);
            }}
            className="w-full flex items-center justify-center gap-0.5 sm:gap-1.5 bg-white hover:bg-slate-50 text-slate-800 px-0.5 py-1.5 sm:px-3.5 sm:py-2 rounded-lg sm:rounded-xl font-semibold text-[8.5px] sm:text-xs uppercase tracking-tight sm:tracking-wider border border-slate-200 transition-all shadow-sm whitespace-nowrap"
          >
            {tripType === 'roundtrip' ? (
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-accent-light"
              >
                <path d="m16 3 4 4-4 4M20 7H4M8 21l-4-4 4-4M4 17h16" />
              </svg>
            ) : (
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-accent-light"
              >
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            )}
            {tripType === 'roundtrip' ? 'Roundtrip' : 'Oneway'}
            <svg
              className="hidden sm:block"
              width="10"
              height="10"
              viewBox="0 0 12 12"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M2 4l4 4 4-4" />
            </svg>
          </button>
          <div
            className={`absolute top-full left-0 mt-2 bg-white border border-slate-200 z-50 rounded-xl shadow-xl min-w-[140px] py-1 overflow-hidden transition-all duration-300 ease-out origin-top-left ${
              showTripDropdown
                ? 'opacity-100 translate-y-0 scale-100 pointer-events-auto'
                : 'opacity-0 -translate-y-2 scale-95 pointer-events-none'
            }`}
          >
            {(['roundtrip', 'oneway'] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => {
                  setTripType(t);
                  setShowTripDropdown(false);
                  setShowPaxDropdown(true);
                }}
                className={`block w-full text-left px-4 py-2.5 text-xs font-semibold tracking-wide uppercase transition-colors ${
                  tripType === t
                    ? 'bg-accent/10 text-accent font-bold'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }`}
              >
                {t === 'roundtrip' ? 'Roundtrip' : 'Oneway'}
              </button>
            ))}
          </div>
        </div>

        {/* Travelers popover */}
        <div ref={paxDropdownRef} className="relative">
          <button
            type="button"
            onClick={() => {
              setShowPaxDropdown(!showPaxDropdown);
              setShowTripDropdown(false);
              setShowClassDropdown(false);
            }}
            className="w-full flex items-center justify-center gap-0.5 sm:gap-1.5 bg-white text-slate-800 hover:bg-slate-50 px-0.5 py-1.5 sm:px-3.5 sm:py-2 rounded-lg sm:rounded-xl font-semibold text-[8.5px] sm:text-xs uppercase tracking-tight sm:tracking-wider border border-slate-200 transition-all shadow-sm min-w-0 sm:min-w-[130px] whitespace-nowrap"
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-accent-light"
            >
              <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
            {totalTravelers} Traveler{totalTravelers !== 1 ? 's' : ''}
            <svg
              className="hidden sm:block"
              width="10"
              height="10"
              viewBox="0 0 12 12"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M2 4l4 4 4-4" />
            </svg>
          </button>
          <div
            className={`absolute top-full left-1/2 -translate-x-1/2 sm:left-0 sm:translate-x-0 mt-2 bg-white border border-slate-200 z-50 rounded-xl shadow-xl p-4 min-w-[240px] transition-all duration-300 ease-out origin-top sm:origin-top-left ${
              showPaxDropdown
                ? 'opacity-100 translate-y-0 scale-100 pointer-events-auto'
                : 'opacity-0 -translate-y-2 scale-95 pointer-events-none'
            }`}
          >
            {[
              {
                label: 'Adults (12+)',
                desc: 'Age 12 or above',
                value: adults,
                setter: setAdults,
                min: 1,
              },
              {
                label: 'Children (2-12)',
                desc: 'Age 2 - 11 years',
                value: children,
                setter: setChildren,
                min: 0,
              },
              {
                label: 'Infants (0-2)',
                desc: 'Under 2 years',
                value: infants,
                setter: setInfants,
                min: 0,
              },
            ].map(({ label, desc, value, setter, min }) => (
              <div key={label} className="flex items-center justify-between mb-3.5">
                <div>
                  <div className="text-slate-800 text-xs font-bold leading-tight">{label}</div>
                  <div className="text-[10px] text-slate-500 font-semibold mt-0.5">{desc}</div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setter(Math.max(min, value - 1))}
                    className="w-7 h-7 bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-lg font-bold flex items-center justify-center transition-colors border border-slate-200"
                  >
                    -
                  </button>
                  <span className="w-5 text-center font-bold text-slate-800 text-sm">{value}</span>
                  <button
                    type="button"
                    onClick={() => setter(value + 1)}
                    className="w-7 h-7 bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-lg font-bold flex items-center justify-center transition-colors border border-slate-200"
                  >
                    +
                  </button>
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={() => {
                setShowPaxDropdown(false);
                setShowClassDropdown(true);
              }}
              className="w-full bg-accent hover:bg-accent-dark text-white py-2 rounded-xl font-bold mt-2 text-xs uppercase tracking-wider transition-colors shadow-sm"
            >
              Done
            </button>
          </div>
        </div>

        {/* Cabin Class Selection */}
        <div ref={classDropdownRef} className="relative">
          <button
            type="button"
            onClick={() => {
              setShowClassDropdown(!showClassDropdown);
              setShowTripDropdown(false);
              setShowPaxDropdown(false);
            }}
            className="w-full flex items-center justify-center gap-0.5 sm:gap-1.5 bg-white text-slate-800 hover:bg-slate-50 px-0.5 py-1.5 sm:px-3.5 sm:py-2 rounded-lg sm:rounded-xl font-semibold text-[8.5px] sm:text-xs uppercase tracking-tight sm:tracking-wider border border-slate-200 transition-all shadow-sm whitespace-nowrap"
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-accent-light"
            >
              <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
            </svg>
            {cabinClass}
            <svg
              className="hidden sm:block"
              width="10"
              height="10"
              viewBox="0 0 12 12"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M2 4l4 4 4-4" />
            </svg>
          </button>
          <div
            className={`absolute top-full right-0 sm:left-0 sm:right-auto mt-2 bg-white border border-slate-200 z-50 rounded-xl shadow-xl min-w-[170px] py-1 overflow-hidden transition-all duration-300 ease-out origin-top-right sm:origin-top-left ${
              showClassDropdown
                ? 'opacity-100 translate-y-0 scale-100 pointer-events-auto'
                : 'opacity-0 -translate-y-2 scale-95 pointer-events-none'
            }`}
          >
            {cabinClasses.map((cls) => (
              <button
                key={cls}
                type="button"
                onClick={() => {
                  setCabinClass(cls);
                  setShowClassDropdown(false);
                  setTimeout(() => originInputRef.current?.focus(), 50);
                }}
                className={`block w-full text-left px-4 py-2.5 text-xs font-semibold tracking-wide uppercase transition-colors ${
                  cabinClass === cls
                    ? 'bg-accent/10 text-accent font-bold'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }`}
              >
                {cls}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Row 2: Structured Multi-Row Layout for maximum legibility inside split columns */}
      <div className="space-y-2 sm:space-y-4 w-full">
        {/* Origin & Destination Inputs Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3.5">
          {/* Origin input container */}
          <div
            ref={originRef}
            className="relative h-14 sm:h-[62px] bg-white border border-transparent hover:border-accent/40 focus-within:border-accent/60 focus-within:ring-2 focus-within:ring-accent/15 rounded-xl px-3.5 transition-all flex items-center shadow-inner"
          >
            <span className="absolute left-3.5 top-1.5 text-[8.5px] font-black uppercase tracking-widest text-slate-500">
              From
            </span>
            <svg
              className="absolute left-3.5 bottom-3.5 text-accent-light"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <path d="M22 2L11 13" />
              <path d="M22 2l-7 20-4-9-9-4 20-7z" />
            </svg>
            <input
              ref={originInputRef}
              type="text"
              placeholder="Origin City / Airport"
              value={origin}
              required
              onChange={(e) => handleOriginChange(e.target.value)}
              className="w-full bg-transparent text-slate-800 placeholder-slate-500 rounded-none text-[13.5px] sm:text-base font-semibold focus:outline-none pt-3 sm:pt-4"
              style={{ paddingLeft: '26px' }}
            />
            {origin.trim().length >= 2 &&
              (originSuggestions.length > 0 || (originHasSearched && !isSearchingOrigin)) && (
                <div className="absolute top-full left-0 right-0 bg-white backdrop-blur-lg z-50 shadow-lg border border-slate-200 mt-1 rounded-xl overflow-hidden animate-fadeInUp">
                  {isSearchingOrigin ? (
                    <div className="px-4 py-3 text-xs font-semibold text-slate-500 flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-accent border-t-transparent rounded-full animate-spin"></div>
                      Searching...
                    </div>
                  ) : originSuggestions.length > 0 ? (
                    originSuggestions.map((a) => (
                      <button
                        key={a.code}
                        type="button"
                        onClick={() => {
                          setOrigin(`${a.name} (${a.code})`);
                          setOriginSuggestions([]);
                          setOriginHasSearched(false);
                          setTimeout(() => destInputRef.current?.focus(), 50);
                        }}
                        className="flex items-center justify-between w-full text-left px-4 py-3 hover:bg-slate-100 transition-colors border-b border-slate-100 last:border-0"
                      >
                        <div className="flex items-center gap-2.5">
                          <span className="text-slate-800 font-bold text-xs">{a.name}</span>
                        </div>
                        <span className="text-xs bg-accent/10 text-accent px-2 py-0.5 rounded font-extrabold shadow-sm border border-accent/20">
                          {a.code}
                        </span>
                      </button>
                    ))
                  ) : (
                    <div className="px-4 py-3 text-xs font-semibold text-slate-500">
                      No results found. Try another city or airport.
                    </div>
                  )}
                </div>
              )}
          </div>

          {/* Destination input container */}
          <div
            ref={destRef}
            className="relative h-14 sm:h-[62px] bg-white border border-transparent hover:border-accent/40 focus-within:border-accent/60 focus-within:ring-2 focus-within:ring-accent/15 rounded-xl px-3.5 transition-all flex items-center shadow-inner"
          >
            <span className="absolute left-3.5 top-1.5 text-[8.5px] font-black uppercase tracking-widest text-slate-500">
              To
            </span>
            <svg
              className="absolute left-3.5 bottom-3.5 text-accent-light"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
            <input
              ref={destInputRef}
              type="text"
              placeholder="Destination City / Airport"
              value={destination}
              required
              onChange={(e) => handleDestChange(e.target.value)}
              className="w-full bg-transparent text-slate-800 placeholder-slate-500 rounded-none text-[13.5px] sm:text-base font-semibold focus:outline-none pt-3 sm:pt-4"
              style={{ paddingLeft: '26px' }}
            />
            {destination.trim().length >= 2 &&
              (destSuggestions.length > 0 || (destHasSearched && !isSearchingDest)) && (
                <div className="absolute top-full left-0 right-0 bg-white backdrop-blur-lg z-50 shadow-lg border border-slate-200 mt-1 rounded-xl overflow-hidden animate-fadeInUp">
                  {isSearchingDest ? (
                    <div className="px-4 py-3 text-xs font-semibold text-slate-500 flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-accent border-t-transparent rounded-full animate-spin"></div>
                      Searching...
                    </div>
                  ) : destSuggestions.length > 0 ? (
                    destSuggestions.map((a) => (
                      <button
                        key={a.code}
                        type="button"
                        onClick={() => {
                          setDestination(`${a.name} (${a.code})`);
                          setDestSuggestions([]);
                          setDestHasSearched(false);
                          setTimeout(
                            () => document.getElementById('depart-date-picker')?.click(),
                            50
                          );
                        }}
                        className="flex items-center justify-between w-full text-left px-4 py-3 hover:bg-slate-100 transition-colors border-b border-slate-100 last:border-0"
                      >
                        <div className="flex items-center gap-2.5">
                          <span className="text-slate-800 font-bold text-xs">{a.name}</span>
                        </div>
                        <span className="text-xs bg-accent/10 text-accent px-2 py-0.5 rounded font-extrabold shadow-sm border border-accent/20">
                          {a.code}
                        </span>
                      </button>
                    ))
                  ) : (
                    <div className="px-4 py-3 text-xs font-semibold text-slate-500">
                      No results found. Try another city or airport.
                    </div>
                  )}
                </div>
              )}
          </div>
        </div>

        {/* Dates & Search CTA Row */}
        <div
          className={`grid gap-2 sm:gap-3.5 ${tripType === 'roundtrip' ? 'grid-cols-2 sm:grid-cols-3' : 'grid-cols-1 sm:grid-cols-2'}`}
        >
          {/* Webkit Dynamic Calendar & Premium Glow button style override */}
          <style
            dangerouslySetInnerHTML={{
              __html: `
            .custom-date-input::-webkit-calendar-picker-indicator {
              background: transparent;
              bottom: 0;
              color: transparent;
              cursor: pointer;
              height: auto;
              left: 0;
              position: absolute;
              right: 10;
              top: 0;
              width: auto;
              z-index: 20;
            }
            .custom-date-input {
              position: relative;
              z-index: 10;
            }

            /* Shining swipe keyframes and sweeping reflection animation */
            @keyframes shine-swipe {
              0% {
                transform: translateX(-120%) skewX(-15deg);
              }
              30% {
                transform: translateX(120%) skewX(-15deg);
              }
              100% {
                transform: translateX(120%) skewX(-15deg);
              }
            }
            .shine-effect {
              animation: shine-swipe 4s infinite ease-in-out;
            }

            /* Glowing button ambient shadows pulse */
            @keyframes premium-pulse {
              0%, 100% {
                box-shadow: 0 0 15px rgba(0, 98, 227, 0.4), 0 0 5px rgba(0, 98, 227, 0.15);
              }
              50% {
                box-shadow: 0 0 25px rgba(0, 98, 227, 0.75), 0 0 12px rgba(0, 98, 227, 0.4);
              }
            }
            .glowing-btn-pulse {
              animation: premium-pulse 3s infinite ease-in-out;
            }
            .glowing-btn-pulse:hover {
              animation: none;
              box-shadow: 0 0 35px rgba(0, 98, 227, 0.95), 0 0 18px rgba(0, 98, 227, 0.55);
            }
          `,
            }}
          />

          {/* Depart Date Container */}
          <DatePicker
            id="depart-date-picker"
            label="Depart Date"
            value={departDate}
            onChange={(val) => {
              setDepartDate(val);
              if (tripType === 'roundtrip') {
                setTimeout(() => {
                  document.getElementById('return-date-picker')?.click();
                }, 150);
              } else {
                setTimeout(() => {
                  document.getElementById('search-flights-btn')?.focus();
                }, 150);
              }
            }}
            minDate={formatDateForInput(new Date())}
            icon={
              <svg
                className="absolute left-3.5 bottom-3.5 text-accent-light"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
            }
          />

          {/* Return Date Container */}
          {tripType === 'roundtrip' && (
            <DatePicker
              id="return-date-picker"
              label="Return Date"
              value={returnDate}
              alignRight={true}
              onChange={(val) => {
                setReturnDate(val);
                setTimeout(() => {
                  document.getElementById('search-flights-btn')?.focus();
                }, 150);
              }}
              minDate={departDate || formatDateForInput(new Date())}
              icon={
                <svg
                  className="absolute left-3.5 bottom-3.5 text-accent-light"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
              }
            />
          )}

          {/* Search Trigger Button Container */}
          <div
            className={`${tripType === 'roundtrip' ? 'col-span-2 sm:col-span-1 mt-1 sm:mt-0' : 'col-span-1 sm:col-span-1 mt-1 sm:mt-0'}`}
          >
            <button
              id="search-flights-btn"
              type="submit"
              className="relative overflow-hidden group w-full h-14 sm:h-[58px] bg-gradient-to-r from-blue-600 via-accent to-blue-700 text-white font-black uppercase text-xs sm:text-sm tracking-widest rounded-xl transition-all duration-300 flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-slate-900 hover:from-blue-500 hover:to-accent-light active:scale-[0.98] glowing-btn-pulse"
            >
              {/* Shining sweep effect */}
              <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/40 to-transparent shine-effect pointer-events-none" />

              {/* Action Vector */}
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-white group-hover:rotate-12 transition-transform duration-300"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <span>Search flights</span>
            </button>
          </div>
        </div>
      </div>
    </form>
  );
}
